import React, { createContext, ReactNode, useContext, useMemo } from 'react';
import i18n, {
  setPersistedLanguagePreference,
  SUPPORTED_LANGUAGES,
} from './i18n';

export interface LocalizationContextProps {
  t: typeof i18n.t;
  changeLanguage: (lang: string) => void;
}

const defaultContextValue: LocalizationContextProps = {
  t: (key: string) => key,
  changeLanguage: () => {},
};

export const LocalizationContext =
  createContext<LocalizationContextProps>(defaultContextValue);

interface LocalizationProviderProps {
  children: ReactNode;
}

export function LocalizationProvider({ children }: LocalizationProviderProps) {
  const changeLang = (lang: string) => {
    const normalizedLanguage = lang.toLowerCase().replace('_', '-');
    const baseLanguage = normalizedLanguage.split('-')[0];
    let supportedLanguage = 'en';

    if (
      SUPPORTED_LANGUAGES.includes(
        normalizedLanguage as (typeof SUPPORTED_LANGUAGES)[number],
      )
    ) {
      supportedLanguage = normalizedLanguage;
    } else if (
      SUPPORTED_LANGUAGES.includes(
        baseLanguage as (typeof SUPPORTED_LANGUAGES)[number],
      )
    ) {
      supportedLanguage = baseLanguage;
    }

    setPersistedLanguagePreference(supportedLanguage);
    i18n.changeLanguage(supportedLanguage);
  };

  const contextValue = useMemo(() => {
    return { t: i18n.t, changeLanguage: changeLang };
  }, []);
  return (
    <LocalizationContext.Provider value={contextValue}>
      {children}
    </LocalizationContext.Provider>
  );
}

export function useLocalization() {
  const context = useContext(LocalizationContext);
  if (context === undefined) {
    throw new Error(
      'useLocalization must be used within a LocalizationProvider',
    );
  }
  return context;
}
